import { axiosToken, simpleAxios } from "@/utils/http";
import type { AxiosResponse } from "axios";
import type { List } from "lodash";
import { defineStore, acceptHMRUpdate } from "pinia"
import { useTapasStore } from "./tapas";
import type { TapasChallenge } from "./interfaces/tapas_interfaces";
import { translate, translateAll } from "@/utils/translate";
import { useEventsStore } from "./events";
import { useContentStore } from "./content";
import { useRoute } from "vue-router";
import { useCalendarStore } from "./calendar";
import { useBudgetStore } from "./budget";

interface Booking_Item {
    id:Number,
    name:String,
    description:String,
	price?:String,
	activity:String,

}

export interface OrganizationStatsKpis {
	organization_id?: number;
	organization_name?: string;
	credits: number;
	credit_amount: number;
	events: number;
	lines: number;
	activities: number;
	credits_per_event: number;
	avg_amount_per_event: number;
}

export interface OrganizationStatsMonthRow {
	organization_id: number;
	organization_name: string;
	year: number;
	month: number;
	period: string;
	credits: number;
	credit_amount: number;
	events: number;
	lines: number;
	credits_per_event: number;
}

export interface OrganizationStatsActivityRow {
	organization_id: number;
	organization_name: string;
	activity_id: number;
	activity_code: string;
	activity_name: string;
	events: number;
	lines: number;
	credits: number;
	credit_amount: number;
	credits_per_event: number;
	avg_amount_per_event: number;
}

export interface OrganizationStatsResponse {
	filters: Record<string, unknown>;
	organizations: Array<{ id: number; name: string; api_key?: string }>;
	totals: OrganizationStatsKpis;
	by_organization: OrganizationStatsKpis[];
	by_month: OrganizationStatsMonthRow[];
	by_activity: OrganizationStatsActivityRow[];
}

export const useBookingStore = defineStore("booking", {
	state: () => ({
		bookings:[],
		booking_items:[] as Booking_Item[],
		booking_questions: [],
		contacts: [],
		booking_info:[],
		booking:{},
		unSavedData:{},
		booking_client_activities:[],
	}),
	actions: {
		async setDefault()
		{
			this.bookings = []
			this.booking_items = [] as Booking_Item[]
			this.booking_questions = []
			this.contacts = []
			this.booking_info = []
			this.booking = {}
			this.unSavedData = {}
			this.booking_client_activities = []
		},
		async fetchBookings (params?: { dateRange?: [Date | null, Date | null] | null; filters?: any, date_field?: string, compact?: boolean })
		{
			// Helper function to format date as YYYY-MM-DD using local time (not UTC)
			const formatDateLocal = (date: Date): string => {
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			};

			// Build query parameters
			const queryParams: any = {};
			
			// Add date range if provided and both dates are set
			if (params?.dateRange && params.dateRange[0] && params.dateRange[1]) {
				// Format dates using local time to avoid timezone conversion issues
				queryParams.date_from = formatDateLocal(params.dateRange[0]);
				queryParams.date_to = formatDateLocal(params.dateRange[1]);
				queryParams.date_field = params.date_field || "date";
			}
			if (params?.compact) {
				queryParams.compact = 1;
			}
			
			await axiosToken("get", "booking/bookings/", queryParams)
			.then((response: AxiosResponse) => {
                console.log(response.data);
                this.bookings = response.data;
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async fetchAnalyticsData (params?: { dateRange?: [Date | null, Date | null] | null }): Promise<{ bookings: any[]; budgets: any[] }>
		{
			const formatDateLocal = (date: Date): string => {
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			};

			const queryParams: Record<string, string> = {};

			if (params?.dateRange && params.dateRange[0] && params.dateRange[1]) {
				queryParams.date_from = formatDateLocal(params.dateRange[0]);
				queryParams.date_to = formatDateLocal(params.dateRange[1]);
			}

			const response = await axiosToken("get", "booking/analytics/", queryParams);
			return {
				bookings: response?.data?.bookings ?? [],
				budgets: response?.data?.budgets ?? [],
			};
		},
		async fetchOrganizationStats(params?: {
			organizationIds?: number[];
			organizationFilterMode?: "include" | "exclude";
			year?: number | null;
			month?: number | null;
		}): Promise<OrganizationStatsResponse> {
			const queryParams: Record<string, string> = {};
			if (params?.organizationIds?.length) {
				queryParams.organization_ids = params.organizationIds.join(",");
			}
			if (params?.organizationFilterMode) {
				queryParams.organization_filter_mode = params.organizationFilterMode;
			}
			if (params?.year != null) {
				queryParams.year = String(params.year);
			}
			if (params?.month != null && params.month > 0) {
				queryParams.month = String(params.month);
			}
			const response = await axiosToken("get", "booking/superadmin/organization-stats/", queryParams);
			return response?.data as OrganizationStatsResponse;
		},
		async fetchBookingsBudgets (params?: { dateRange?: [Date | null, Date | null] | null; filters?: any, compact?: boolean })
		{
			// Helper function to format date as YYYY-MM-DD using local time (not UTC)
			const formatDateLocal = (date: Date): string => {
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			};

			const calendarStore = useCalendarStore();
			const budgetStore = useBudgetStore();
			
			// Build query parameters
			const queryParams: any = {};
			
			// Add date range if provided and both dates are set
			if (params?.dateRange && params.dateRange[0] && params.dateRange[1]) {
				// Format dates using local time to avoid timezone conversion issues
				queryParams.date_from = formatDateLocal(params.dateRange[0]);
				queryParams.date_to = formatDateLocal(params.dateRange[1]);
			}
			if (params?.compact) {
				queryParams.compact = 1;
			}
			
			// Fetch both bookings and budgets in parallel
			try {
				const [bookingsResponse, budgetsResponse] = await Promise.all([
					axiosToken("get", "booking/bookings/", queryParams),
					axiosToken("get", "budget/budgets/", {...queryParams, date_field: "date"})
				]);
				
				if (bookingsResponse) {
					console.log(bookingsResponse.data);
					this.bookings = bookingsResponse.data;
					calendarStore.calendarData = {
						...calendarStore.calendarData,
						bookings: bookingsResponse.data
					};
				}
				
				if (budgetsResponse) {
					budgetStore.budgets = budgetsResponse.data;
					calendarStore.calendarData = {
						...calendarStore.calendarData,
						budgets: budgetsResponse.data
					};
				}
			} catch (e: any) {
				console.log(e);
			}
		},
		async fetchBookingInfo(id:Number)
		{
			await axiosToken("get","booking/booking_info/" + id).then((response)=>{
				this.booking_info = response.data
			})
		},
		async fetchBookingHistory(id: number) {
			const response = await axiosToken("get", "booking/booking_info/" + id)
			return {
				clientBookingEdit: Array.isArray(response.data?.clientBookingEdit)
					? response.data.clientBookingEdit
					: [],
				staffBookingEdit: Array.isArray(response.data?.staffBookingEdit)
					? response.data.staffBookingEdit
					: [],
			}
		},
		async fetchBookingFileArchive(id: number) {
			const response = await axiosToken("get", "booking/file_archive/" + id)
			return Array.isArray(response.data) ? response.data : []
		},
		async fetchBookingClientActivities(api_key:String)
		{
			await simpleAxios("get","booking/booking_activities/" + api_key).then((response)=>{
				this.booking_client_activities = response.data
			})
		},
		async fetchBookingActivitiesById(id: number | string)
		{
			await axiosToken("get", "booking/booking_activities_by_id/" + id).then((response)=>{
				this.booking_client_activities = response.data
			})
		},
		async fetchBookingClient(api_key:String)
		{
			await simpleAxios("get","booking/booking/" + api_key).then((response)=>{
				console.log(response.data);
				
				this.booking = response.data
			})
		},
		async fetchUnSavedData(api_key:String)
		{
			await simpleAxios("get","booking/check_edits/" + api_key).then((response)=>{
				console.log(response.data);
				
				this.unSavedData = response.data
			})
		},
		async fetchBookingItems(id_client:Number)
		{
			await axiosToken("get","booking/get_total_billed/" + id_client).then((response)=>{
				this.booking.total_billed = response.data
				return response.data
			})
		},
		async fetchMyStaffSalary(id_booking:Number)
		{
			return await axiosToken("get","booking/staff_salary/" + id_booking).then((response: AxiosResponse)=>{
				return response.data
			})
		},
		newLine(pax = 0){
			if (!this.booking_info || Array.isArray(this.booking_info)) {
				this.booking_info = {} as any
			}
			if (!Array.isArray((this.booking_info as any).bookingItem)) {
				(this.booking_info as any).bookingItem = []
			}

			(this.booking_info as any).bookingItem.push({
				id:-1,
				name:"",
				pax:pax,
				activity:"",
			})
		},
	},
	persist: {
		// paths: ["events"]
	},
	getters:{
		getOptionsTags:  state => (activity: number) => {
			// NEED ACTIVITY FROM THE OTHER STORE
			const contentStore = useContentStore();
			const activityData = contentStore.getContentByTypeById("activity", activity)
			const tags = []
			if( Array.isArray(activityData.price_rules))
			{
				activityData.price_rules.forEach(e =>{
					tags.push({
						value:e.name,
						label:e.name
					})
				})

			}
			return tags
		},
		getPricing:state =>(activity: number, pax:number, tag:String)=>{
			// NEED ACTIVITY FROM THE OTHER STORE
			const contentStore = useContentStore();
			const activityData = contentStore.getContentByTypeById("activity", activity)
			let price = 0;

			if(tag == 'Custom')
			{
				return price
			}

			if( Array.isArray(activityData.price_rules))
			{
				console.log("INNNNNN")
				const priceArray = activityData.price_rules.find(e=> e.name == tag)
				for (let index = 0; index < priceArray.list.length; index++) {
					const element = priceArray.list[index];
					console.log(element);
					
					if(pax >=  parseInt(element.pax_low)  && pax <= parseInt(element.pax_high))
					{
						price = element.price_total
						break;
					}
				}
			}
			console.log(pax,price);
			return price; 
		},
		getPaxInterval:state =>(activity: number, tag:String)=>{
			const contentStore = useContentStore();
			const activityData = contentStore.getContentByTypeById("activity", activity)
			const tagPax = []

			if(tag == 'Custom')
			{
				return ''
			}

			if( Array.isArray(activityData.price_rules))
			{
				const priceArray = activityData.price_rules.find(e=> e.name == tag)
				for (let index = 0; index < priceArray.list.length; index++) {
					const element = priceArray.list[index];
					tagPax.push({
						"label": element.pax_low + ' - ' + element.pax_high,
						"value": index,
					})
				}
			}
			return tagPax
		},
		getPriceByIndex:state =>(activity: number, index:number, tag:String)=>{
			const contentStore = useContentStore();
			const activityData = contentStore.getContentByTypeById("activity", activity)
			let price = 0;
			if( Array.isArray(activityData.price_rules))
			{
				const priceArray = activityData.price_rules.find(e=> e.name == tag)
				price = priceArray.list[index].price_total
			}
			console.log(price);
			return price; 
		}
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useBookingStore, import.meta.hot))
}
